import AnalyticsEventName from './analyticsEventName';
import type { IAnalyticsAdditionalEventData } from './definitions';

declare const amplitude: {
    track: (event: {
        event_type: string;
        event_properties?: Record<string, unknown>;
        groups?: Partial<Record<'Teams' | 'Projects', string>>;
    }) => void;
    setUserId(userId: undefined | string): void;
    setTransport(transport: 'beacon' | 'fetch'): void;
    flush(): void;
};
declare const analyticsAdditionalEventData: IAnalyticsAdditionalEventData;
declare const amplitudeData: {
    userId: string | undefined;
};

const sanitizeUserId = (userId: string | number | null | undefined): string | undefined => {
    if (!userId) return undefined;

    return String(userId).padStart(5, '0');
};

const isExcludedTeamAndProjectEvent = (eventName: AnalyticsEventName) =>
    [
        AnalyticsEventName.SIGNED_IN.toString(),
        AnalyticsEventName.SIGNED_OUT.toString(),
        AnalyticsEventName.REGISTRATION_FORM_COMPLETED.toString(),
    ].includes(eventName.toString());

export const setAmplitudeTransport = (transport: 'fetch' | 'beacon' = 'fetch') => {
    const amplitudeLoaded = typeof amplitude !== 'undefined';

    if (!amplitudeLoaded) {
        return;
    }

    amplitude.setTransport(transport);
    amplitude.flush();
};

const track = (eventName: AnalyticsEventName, eventProperties = {}, userId: string | null = null) => {
    const amplitudeLoaded = typeof amplitude !== 'undefined';

    if (!amplitudeLoaded) {
        return;
    }

    // Use argument user id as a priority in case we need to force pass it
    // This is a rare occurrence and only happens with calls where context
    // from backend session has not been set, like signup
    const externalUserId = userId || amplitudeData?.userId;
    if (externalUserId) {
        const sanitizedUserId = sanitizeUserId(externalUserId);
        amplitude.setUserId(sanitizedUserId);
    }

    const includeAdditionalEventData =
        typeof analyticsAdditionalEventData !== 'undefined' && !isExcludedTeamAndProjectEvent(eventName);

    try {
        if (amplitudeLoaded) {
            if (includeAdditionalEventData) {
                const groups = analyticsAdditionalEventData && {
                    ...(analyticsAdditionalEventData.project?.project_id && {
                        Projects: analyticsAdditionalEventData.project.project_id.toString(),
                    }),
                    ...(analyticsAdditionalEventData.team?.team_id && {
                        Teams: analyticsAdditionalEventData.team.team_id.toString(),
                    }),
                };

                amplitude.track({
                    event_type: eventName.toString(),
                    event_properties: eventProperties,
                    groups,
                });
            } else {
                amplitude.track({ event_type: eventName.toString(), event_properties: eventProperties });
            }
        }
    } catch (e) {
        // Just in case
    }
};

export default track;
